/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloClient, gql } from '@apollo/client'
import getApolloHygraphClient from 'src/apollo-hygraph-client'
import { PillarPage } from 'src/constants/searchPages'
import { Language } from 'src/types/graphql'
import { IPost, IQuery, PostOrderByInput } from 'src/types/hygraph'
import {
  IArticleSearchable,
  IHelpSearchEntry,
  RedirectUrls
} from 'src/types/types'
import { throttledFetch } from './rateLimit'
import anfitrion from '/public/svgs/House.svg'
import security from '/public/svgs/blue-shield.svg'
import renteros from '/public/svgs/box.svg'
import faqs from '/public/svgs/help.svg'
// Square brand mark, NOT the wordmark: the wordmark in a 48px tile rendered as a dash.
import spotme from '/public/svgs/spotme-mark.svg'
import account from '/public/svgs/user.svg'

export enum HygraphTopics {
  BlogMiniBodega = 'blog-mini-bodega',
  BlogParking = 'blog-parking',
  BlogBodega = 'blog-bodega',
  BlogNave = 'blog-nave',
  BlogHost = 'blog-host',
  HelpHost = 'anfitrion',
  HelpRent = 'rentero',
  HelpFaq = 'faqs',
  HelpAboutUs = 'spotme',
  HelpSecurity = 'seguridad',
  HelpMyAccount = 'cuenta'
}

export type IBlogPillar = PillarPage | 'anfitrion'

export const HELP_CENTER_TOPICS: HygraphTopics[] = [
  HygraphTopics.HelpHost,
  HygraphTopics.HelpRent,
  HygraphTopics.HelpFaq,
  HygraphTopics.HelpAboutUs,
  HygraphTopics.HelpSecurity,
  HygraphTopics.HelpMyAccount
]

export const PILLAR_BLOG_TOPICS: HygraphTopics[] = [
  HygraphTopics.BlogMiniBodega,
  HygraphTopics.BlogParking,
  HygraphTopics.BlogBodega,
  HygraphTopics.BlogNave
]

export const BLOG_TOPICS: HygraphTopics[] = [
  HygraphTopics.BlogMiniBodega,
  HygraphTopics.BlogParking,
  HygraphTopics.BlogBodega,
  HygraphTopics.BlogNave,
  HygraphTopics.BlogHost
]

export const PILLAR_BLOG_TOPIC: { [key in IBlogPillar]?: HygraphTopics } = {
  [PillarPage.SelfStorage]: HygraphTopics.BlogMiniBodega,
  [PillarPage.Parking]: HygraphTopics.BlogParking,
  [PillarPage.Warehouse]: HygraphTopics.BlogBodega,
  [PillarPage.Industrial]: HygraphTopics.BlogNave,
  anfitrion: HygraphTopics.BlogHost
}

const metaImg = 'https://www.spotme.mx/images/cover.png'

const getPath = (topic: HygraphTopics) =>
  `${RedirectUrls.Help}/${topic}` as RedirectUrls

export const TOPIC_MAP: {
  [key in HygraphTopics]: {
    path: RedirectUrls
    topic: HygraphTopics
    label: string
    image?: string
    title: string
    description: string
    metaImg: string
    hygraphId: string
    pillar?: PillarPage | 'anfitrion'
  }
} = {
  [HygraphTopics.BlogMiniBodega]: {
    path: RedirectUrls.Blog,
    topic: HygraphTopics.BlogMiniBodega,
    label: 'Mini Bodegas',
    title: '',
    description: '',
    metaImg,
    hygraphId: 'cmbks5hbf6mch07lggxt17xuj',
    pillar: PillarPage.SelfStorage
  },
  [HygraphTopics.BlogParking]: {
    path: RedirectUrls.Blog,
    topic: HygraphTopics.BlogParking,
    label: 'Estacionamientos y Pensiones',
    title: '',
    description: '',
    metaImg,
    hygraphId: 'cmbks6num6xs707jzj3mwwmxm',
    pillar: PillarPage.Parking
  },
  [HygraphTopics.BlogBodega]: {
    path: RedirectUrls.Blog,
    topic: HygraphTopics.BlogBodega,
    label: 'Bodegas Comerciales',
    title: '',
    description: '',
    metaImg,
    hygraphId: 'cmbks6z1x71j507k27u2r94rh',
    pillar: PillarPage.Warehouse
  },
  [HygraphTopics.BlogNave]: {
    path: RedirectUrls.Blog,
    topic: HygraphTopics.BlogNave,
    label: 'Naves Industriales',
    title: '',
    description: '',
    metaImg,
    hygraphId: 'cmbks7inr71m407k2n42jrh3p',
    pillar: PillarPage.Industrial
  },
  [HygraphTopics.BlogHost]: {
    path: RedirectUrls.Blog,
    topic: HygraphTopics.BlogHost,
    label: 'Anfitriones',
    title: '',
    description: '',
    metaImg,
    hygraphId: 'cmbpdw8eg2nto07ljplktzeye',
    pillar: 'anfitrion'
  },
  [HygraphTopics.HelpHost]: {
    path: getPath(HygraphTopics.HelpHost),
    topic: HygraphTopics.HelpHost,
    label: 'Anfitriones',
    image: anfitrion,
    title: 'Información General Anfitriones SpotMe, Gana Sin Esfuerzo',
    description:
      'Gana dinero extra con tu espacio de almacenamiento. Pensiones, estacionamientos, mini-bodegas y más. Comision dinamica. ¡Es fácil!',
    metaImg,
    hygraphId: 'cltnkv6e21ekr07liq7esyi4y'
  },
  [HygraphTopics.HelpRent]: {
    path: getPath(HygraphTopics.HelpRent),
    topic: HygraphTopics.HelpRent,
    label: 'Renteros',
    image: renteros,
    title: 'SpotMe para Renteros - Seguridad y Conveniencia',
    description:
      'Encuentra tu espacio cerca de casa o donde lo requieras. ¡Hasta 30% más económico que almacenamientos tradicionales! Proceso de arrendamiento 6X más rápido.',
    metaImg,
    hygraphId: 'cltnkw35v1eky07le0mlnu9j4'
  },
  [HygraphTopics.HelpFaq]: {
    path: getPath(HygraphTopics.HelpFaq),
    topic: HygraphTopics.HelpFaq,
    label: 'Preguntas Frecuentes',
    image: faqs,
    title: 'En SpotMe Resolvemos Todas tus Preguntas e Inquietudes',
    description:
      '¿Es SpotMe seguro? ¡SI! Contamos con un proceso de verificación detallado. Esta y más respuestas en la sección de FAQs SpotMe.',
    metaImg,
    hygraphId: 'cltnkwepc1el907le3kadp3hu'
  },
  [HygraphTopics.HelpAboutUs]: {
    path: getPath(HygraphTopics.HelpAboutUs),
    topic: HygraphTopics.HelpAboutUs,
    label: 'Acerca de Nosotros',
    image: spotme,
    title: 'Acerca de SpotMe - Marketplace de Almacenamiento',
    description:
      'SpotMe, primer marketplace de almacenamiento Mexicano. Encuentra el espacio ideal de almacenamiento personal y de negocios. Se parte de la comunidad.',
    metaImg,
    hygraphId: 'cltnkwv7v1eme07liax8duj0s'
  },
  [HygraphTopics.HelpSecurity]: {
    path: getPath(HygraphTopics.HelpSecurity),
    topic: HygraphTopics.HelpSecurity,
    label: 'Seguridad',
    image: security,
    title: 'SpotMe es Seguro, Revisión Detallada de Perfiles',
    description:
      'SpotMe, primer marketplace de almacenamiento Mexicano. Todos los miembros de la comunidad SpotMe deben tener un proceso de verificación de identidad.',
    metaImg,
    hygraphId: 'cltnkx6c31hug07lknrli8hs6'
  },
  [HygraphTopics.HelpMyAccount]: {
    path: getPath(HygraphTopics.HelpMyAccount),
    topic: HygraphTopics.HelpMyAccount,
    label: 'Mi Cuenta',
    image: account,
    title: 'Tu Cuenta en SpotMe - Gestiona tu Perfil y Reservaciones',
    description:
      'Administra tu cuenta SpotMe: perfil, pagos, reservaciones y configuración. Ahorra hasta 30% en pensión para autos, bodegas, oficinas y más.',
    metaImg,
    hygraphId: 'cltnkxihr1hrw07latyadoa50'
  }
}

export function getPostTopic(post: IPost) {
  const topicId = post.topic?.[0]?.id
  const topic = topicId
    ? Object.values(TOPIC_MAP).find((t) => t.hygraphId === topicId)
    : null
  const { pillar } = topic ?? {}
  const basePath = pillar ? `/${pillar}/blog` : null
  return { topic, basePath }
}

export const TOPIC_ID_MAP: { [key: string]: HygraphTopics } = Object.values(
  HygraphTopics
).reduce(
  (acc, topic) => {
    const id = TOPIC_MAP[topic].hygraphId
    acc[id] = topic
    return acc
  },
  {} as { [key: string]: HygraphTopics }
)

const POST_LIST_FIELDS = `
  id
  title
  titleOg
  slug
  description
  descriptionOg
  date
  publishedAt
  createdAt
  updatedAt
  coverImage {
    url
  }
  coverImageAlt
  author {
    name
  }
  topic {
    id
  }
`

const GET_POSTS = gql`
  query GetPosts(
    $first: Int!
    $skip: Int!
    $orderBy: PostOrderByInput!
    $where: PostWhereInput!
  ) {
    posts(first: $first, skip: $skip, orderBy: $orderBy, where: $where) {
      ${POST_LIST_FIELDS}
      content {
        raw
      }
      structuredData
    }
    postsConnection(where: $where) {
      aggregate {
        count
      }
    }
  }
`

const GET_POSTS_LIST = gql`
  query GetPostsList(
    $first: Int!
    $skip: Int!
    $orderBy: PostOrderByInput!
    $where: PostWhereInput!
  ) {
    posts(first: $first, skip: $skip, orderBy: $orderBy, where: $where) {
      ${POST_LIST_FIELDS}
    }
    postsConnection(where: $where) {
      aggregate {
        count
      }
    }
  }
`

const GET_POST = gql`
  query GetPost($slug: String!) {
    post(where: { slug: $slug }) {
      id
      title
      titleOg
      slug
      description
      descriptionOg
      date
      publishedAt
      createdAt
      updatedAt
      coverImage {
        url
      }
      coverImageAlt
      content {
        raw
      }
      author {
        name
      }
      topic {
        id
      }
      structuredData
    }
  }
`

// Cache for posts during build time
const CACHE_TIME = 1000 * 60 * 30 // 30 minutes
let fetchTime = 0
let status: 'requested' | 'fetched' | null = null
export const resetBlogCache = () => {
  status = null
}

const pillarPostCache: {
  [topic in HygraphTopics]: {
    [slug: string]: { [key in Language]: IPost | null }
  }
} = Object.values(HygraphTopics).reduce(
  (acc, topic) => {
    acc[topic] = {}
    return acc
  },
  {} as {
    [topic in HygraphTopics]: {
      [slug: string]: { [key in Language]: IPost | null }
    }
  }
)

export async function getAllPosts(locale: Language) {
  const client: ApolloClient = getApolloHygraphClient()
  const PAGE_SIZE = 250 // Maximum safe page size for GraphQL queries
  let allPosts: IPost[] = []
  let hasMore = true
  let skip = 0

  while (hasMore) {
    const response = await throttledFetch(client, {
      query: GET_POSTS,
      variables: {
        first: PAGE_SIZE,
        skip,
        orderBy: PostOrderByInput.UpdatedAtAsc,
        where: {}
      },
      context: {
        headers: {
          'gcms-locales': locale === 'en' ? [locale, 'es'] : [locale, 'en']
        }
      }
    })

    const { posts, postsConnection } = response.data as QueryCombinedResult
    allPosts = [...allPosts, ...posts]

    // Check if we need to fetch more posts
    const totalPosts = postsConnection?.aggregate?.count ?? 0
    hasMore = allPosts.length < totalPosts
    skip += PAGE_SIZE
  }

  // Cache posts by topic
  allPosts.forEach((post) => {
    const res = getPostTopic(post)
    const topic = res?.topic?.topic

    if (topic) {
      if (!pillarPostCache[topic][post.slug]) {
        pillarPostCache[topic][post.slug] = {
          [Language.Es]: null,
          [Language.En]: null
        }
      }
      pillarPostCache[topic][post.slug][locale] = post
    }
  })

  return {
    posts: allPosts,
    postsConnection: { aggregate: { count: allPosts.length } }
  }
}

// Editorial `date` field is the curated "this article is dated X" — must win
// over Hygraph's auto-set timestamps. A 2024-created post refreshed for 2026
// should rank by its 2026 `date`, not by when Hygraph first inserted it or
// when it was last re-published. publishedAt/createdAt/updatedAt remain as
// fallbacks for posts that somehow lack a `date`.
const getSortableDate = (post: IPost | null) =>
  new Date(
    post?.date ?? post?.publishedAt ?? post?.createdAt ?? post?.updatedAt ?? ''
  ).getTime()

const sortPosts = (a: IPost | null, b: IPost | null) =>
  getSortableDate(b) - getSortableDate(a)

const getPostsByTopic = (topic: HygraphTopics, locale: Language) => {
  const topicId = TOPIC_MAP[topic].hygraphId
  const { Es, En } = Language
  return Object.values(pillarPostCache[topic])
    .map((post) => post[locale] ?? post[Es] ?? post[En])
    .filter((post) => post?.topic?.[0]?.id === topicId)
    .filter((post) => post !== null)
    .sort(sortPosts)
}

const stripContent = (post: IPost): IPost => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  const { content, structuredData, ...rest } = post
  return rest as IPost
}

export async function getPosts(
  first: 1000 | 7 | 6,
  skip: number,
  orderBy: PostOrderByInput,
  where?: {
    isBlog?: boolean
    isHelp?: boolean
    topic?: HygraphTopics
    locale?: string
    listOnly?: boolean
  }
): Promise<any> {
  if (
    status === null ||
    (status === 'fetched' && Date.now() - fetchTime > CACHE_TIME)
  ) {
    status = 'requested'
    await Promise.all([getAllPosts(Language.Es), getAllPosts(Language.En)])
    status = 'fetched'
    fetchTime = Date.now()
  } else if (status === 'requested') {
    while (status === 'requested') {
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
  }

  const client: ApolloClient = getApolloHygraphClient()
  const { topic, locale = Language.Es, isBlog, isHelp, listOnly } = where ?? {}
  const topicId = topic ? TOPIC_MAP[topic]?.hygraphId : null
  const maybeStrip = (posts: IPost[]) =>
    listOnly ? posts.map(stripContent) : posts

  if (topic && locale) {
    const allPosts = getPostsByTopic(topic, locale as Language).sort(sortPosts)
    const posts = allPosts.slice(skip, skip + first)
    if (posts.length > 0) {
      return {
        data: {
          posts: maybeStrip(posts),
          postsConnection: { aggregate: { count: allPosts.length } }
        }
      } as any
    }
  }

  if (isBlog) {
    const topics = [
      HygraphTopics.BlogBodega,
      HygraphTopics.BlogHost,
      HygraphTopics.BlogMiniBodega,
      HygraphTopics.BlogParking,
      HygraphTopics.BlogNave
    ]
    const merged = topics
      .map((topic) => getPostsByTopic(topic, locale as Language))
      .flat()
    const seenSlugs = new Set<string>()
    const allPosts = merged
      .filter((post) => {
        const slug = post?.slug
        if (!slug) return false
        if (seenSlugs.has(slug)) return false
        seenSlugs.add(slug)
        return true
      })
      .sort(sortPosts)
    const posts = allPosts.slice(skip, skip + first)
    if (posts.length > 0) {
      return {
        data: {
          posts: maybeStrip(posts),
          postsConnection: { aggregate: { count: allPosts.length } }
        }
      } as any
    }
  }

  if (isHelp) {
    const merged = HELP_CENTER_TOPICS.map((topic) =>
      getPostsByTopic(topic, locale as Language)
    ).flat()
    const seenSlugs = new Set<string>()
    const allPosts = merged
      .filter((post) => {
        const slug = post?.slug
        if (!slug) return false
        if (seenSlugs.has(slug)) return false
        seenSlugs.add(slug)
        return true
      })
      .sort(sortPosts)
    const posts = allPosts.slice(skip, skip + first)
    if (posts.length > 0) {
      return {
        data: {
          posts: maybeStrip(posts),
          postsConnection: { aggregate: { count: allPosts.length } }
        }
      } as any
    }
  }

  // Make the API call with the specific filters
  const response = await throttledFetch(client, {
    query: listOnly ? GET_POSTS_LIST : GET_POSTS,
    variables: {
      first,
      skip,
      orderBy,
      where: {
        ...(topicId && { topic_every: { id: topicId } })
      }
    },
    context: {
      headers: {
        'gcms-locales': locale === 'en' ? [locale, 'es'] : [locale, 'en']
      }
    }
  })

  return response
}

// Modified getPost to use cache
export async function getPost(slug: string, locale: string = Language.Es) {
  if (
    status === null ||
    (status === 'fetched' && Date.now() - fetchTime > CACHE_TIME)
  ) {
    status = 'requested'
    await Promise.all([getAllPosts(Language.Es), getAllPosts(Language.En)])
    status = 'fetched'
    fetchTime = Date.now()
  } else if (status === 'requested') {
    while (status === 'requested') {
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
  }

  const { Es, En } = Language
  let found: IPost | null = null
  Object.values(HygraphTopics).forEach((topic) => {
    const post = pillarPostCache[topic][slug]
    if (post) {
      const blog = post[locale as Language] ?? post[Es] ?? post[En]
      if (blog) {
        found = blog
      }
    }
  })

  if (found) {
    return { data: { post: found } } as any
  }

  const client: ApolloClient = getApolloHygraphClient()

  // Make the API call for the specific post
  const response = await throttledFetch(client, {
    query: GET_POST,
    variables: { slug },
    context: {
      headers: {
        'gcms-locales': locale === 'en' ? [locale, 'es'] : [locale, 'en']
      }
    }
  })

  return response
}

export type QueryCombinedResult = Pick<IQuery, 'posts' | 'postsConnection'>

export const convertPost = ({
  title,
  description,
  slug,
  ...rest
}: IPost): IArticleSearchable => {
  const { id } =
    rest.topic && rest.topic[0] ? rest.topic[0] : { id: TOPIC_ID_MAP.blog }
  const topic = TOPIC_ID_MAP[id]
  const href = `${TOPIC_MAP[topic]?.path}/${slug}`
  return {
    title: title ?? '',
    description: description ?? '',
    href: href as RedirectUrls,
    body: ''
  }
}

const collectKeywordsFromStructuredData = (post: IPost): string[] => {
  const sd = Array.isArray(post.structuredData) ? post.structuredData : []
  const out: string[] = []
  for (const entry of sd) {
    if (!entry || typeof entry !== 'object') continue
    if (entry.type === 'FAQ' && Array.isArray(entry.items)) {
      for (const item of entry.items) {
        if (item?.title) out.push(item.title)
      }
    } else if (entry.type === 'seoMeta') {
      if (entry.primaryKeyword) out.push(entry.primaryKeyword)
      if (Array.isArray(entry.secondaryKeywords))
        out.push(...entry.secondaryKeywords)
    }
  }
  return out
}

/**
 * Build a slim search index for the help-center hero search.
 * Concatenates title + description + FAQ question titles + primary/secondary
 * keywords from structuredData into a single lowercased `searchText` field
 * per article. ~150 bytes per entry, ~7KB total — fine to ship in props.
 * See scripts/hygraph/structured-data.md §10.3.
 */
export const buildHelpSearchIndex = (
  posts: IPost[] | undefined
): IHelpSearchEntry[] => {
  if (!Array.isArray(posts)) return []
  return posts.map((post) => {
    const { title, description, href } = convertPost(post)
    const keywords = collectKeywordsFromStructuredData(post)
    const searchText = [title, description, ...keywords]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return { title, description, href, searchText }
  })
}
